import FileUpload from "./FileUpload";
import { ClientProcessComment } from "@avenueops/shared-types";
import { Avatar } from "@avenueops/web-components";
import { CommentType } from "@prisma/client";
import MarkdownMessageText from "components/generic-building-blocks/MarkdownMessageText";
import _ from "lodash";
import React from "react";

interface Props {
  comment: ClientProcessComment;
  small?: boolean;
}

export default function CommentComponent({ comment, small }: Props) {
  const createdAtDate = new Date(comment.createdAt);

  return (
    <div className={`flex rounded-lg mt-1 pt-3 pb-2 items-start w-full px-2 ${comment.commentType === CommentType.INTERNAL ? "bg-primary-light" : ""}`}>
      {/* If there is no user assume this is a system message and use the Avenue Logo as the Avatar */}
      <Avatar
        size={small ? "DEFAULT" : "LARGE"}
        src={comment.isSystemMessage ? require("../../../public/avenue_icon_black.svg") : comment.user?.image ?? undefined}
      />
      {comment.commentType === CommentType.PUBLIC && comment.inboundMessage ? (
        <div className="flex flex-col ml-4 w-full">
          <div className={`flex mb-1 max-w-[80%] ${small ? "text-sm flex-col" : ""}`}>
            <div className="text-gray-900 font-bold mr-1 whitespace-nowrap">{comment.inboundMessage.sender}</div>
            <div className="flex gap-1 w-full">
              <div className="text-gray-500 whitespace-nowrap">
                {`via ${_.capitalize(comment.inboundMessage?.sourceType ?? "")} ${_.lowerCase(comment.inboundMessage.messageType)}`}
              </div>
              {comment.inboundMessage.remoteId && comment.inboundMessage.slackChannelName && !small ? (
                <div className="text-gray-500 lg:inline-block hidden truncate">
                  in{" "}
                  <a
                    className="text-black hover:underline font-medium cursor-pointer"
                    target="_blank"
                    rel="noreferrer"
                    href={getChannelRedirectUrl({
                      slackId: comment.inboundMessage.remoteId,
                      inboundPermalink: comment.inboundMessage.permalink ?? undefined,
                      outboundPermalink: comment.inboundMessage.outboundMessage?.permalink ?? undefined,
                    })}
                  >
                    {comment.inboundMessage.slackChannelName}
                  </a>
                </div>
              ) : null}
              <div className="text-gray-500 sm:ml-1 whitespace-nowrap">
                <span className="sm:inline hidden">• </span>
                <span className="truncate">{createdAtDate.toLocaleString()}</span>
              </div>
            </div>
          </div>
          <span className="flex flex-col">
            <MarkdownMessageText className={`prose text-gray-900 block max-w-[80%] ${small ? "text-sm" : null}`} message={comment.message} />
            {comment.inboundMessage.edited && <span className={`${small ? "text-xs" : "text-sm"} text-gray-500`}>(edited)</span>}
            {comment.attachments?.length > 0 && (
              <div>
                {comment.attachments.map((fileUpload) => (
                  <FileUpload key={fileUpload.id} fileUpload={fileUpload} />
                ))}
              </div>
            )}
          </span>
        </div>
      ) : (
        <div className={`flex flex-col ml-4 w-full ${small ? "text-sm" : null}`}>
          <span className={`flex mb-1 max-w-[80%] ${small ? "flex-col" : null}`}>
            <div className={`text-gray-900 font-bold  mr-1 whitespace-nowrap truncate`}>
              {comment.isSystemMessage ? "Avenue Bot" : `${comment.user?.firstName} ${comment.user?.lastName}`}
            </div>
            <div className={`text-gray-500  whitespace-nowrap`}>
              <span className={small ? "hidden" : "inline"}>• </span>
              <span>{createdAtDate.toLocaleString()}</span>
              {comment.commentType === CommentType.INTERNAL ? (
                <span className={`${small ? "text-xs" : "text-sm"} text-gray-500`}> (internal note) </span>
              ) : null}
            </div>
          </span>
          <MarkdownMessageText className="prose text-gray-900 block max-w-[80%] overflow-auto" message={comment.message} />
          {comment.attachments?.length > 0 && (
            <span className="flex flex-col">
              {comment.attachments.map((fileUpload) => (
                <FileUpload key={fileUpload.id} fileUpload={fileUpload} />
              ))}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Get the Slack channel redirect URL from slackId
function getChannelRedirectUrl({ slackId, outboundPermalink, inboundPermalink }: { slackId: string; outboundPermalink?: string; inboundPermalink?: string }) {
  if (inboundPermalink) return inboundPermalink;
  if (outboundPermalink) return outboundPermalink;

  const channelId = slackId.split("-")[1];
  return `https://slack.com/app_redirect?channel=${channelId}`;
}
